import { useState, useEffect, useCallback } from 'react';
import { useUseCelo } from '../../../providers/UseCeloProvider';
import type {
  PaymentMethodDisplay,
  UsePaymentMethodsReturn
} from '../../../types/subscription-manager.types';
import type { PaymentMethod } from '../../../types/payment';
import { SubscriptionError } from '../../../types/subscription-manager.types';

/**
 * Custom hook for payment methods management
 * Handles fetching, adding, removing, and updating payment methods
 */
export function usePaymentMethods(): UsePaymentMethodsReturn {
  const [methods, setMethods] = useState<PaymentMethodDisplay[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<SubscriptionError | null>(null);

  const {
    paymentClient,
  } = useUseCelo();


  // Fetch payment methods
  const fetchPaymentMethods = useCallback(async (): Promise<void> => {
    if (!paymentClient) {
      setError(new SubscriptionError('Payment client not available', 'PAYMENT_CLIENT_MISSING', 400));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const paymentMethods = await paymentClient.getPaymentMethods();

      // Transform to PaymentMethodDisplay format
      const displayMethods: PaymentMethodDisplay[] = paymentMethods.map((method: PaymentMethod) => ({
        ...method,
        email: undefined, // Will be populated for PayPal methods
        bankName: undefined, // Will be populated for bank account methods
        createdAt: new Date(), // Will be updated with actual creation date
      }));

      setMethods(displayMethods);
    } catch (err: unknown) {
      const subscriptionError = err instanceof SubscriptionError
        ? err
        : new SubscriptionError(
            err instanceof Error ? err.message : 'Failed to fetch payment methods',
            'FETCH_ERROR',
            500
          );
      setError(subscriptionError);
      setMethods([]);
    } finally {
      setLoading(false);
    }
  }, [paymentClient]);

  // Add new payment method
  const addMethod = useCallback(async (): Promise<void> => {
    if (!paymentClient) {
      throw new SubscriptionError('Add payment method not available', 'ADD_NOT_AVAILABLE', 400);
    }

    try {
      setError(null);

      // This typically redirects to Stripe Checkout or opens a modal
      // The actual payment method will be added through webhooks
      const result = await paymentClient.addPaymentMethod();

      // If it returns a URL, redirect user
      if ('url' in result && result.url) {
        window.location.href = result.url;
      } else {
        // For modal flows, the payment method will be added via webhooks
        // We'll refetch after a short delay to catch the update
        setTimeout(() => {
          fetchPaymentMethods();
        }, 3000);
      }
    } catch (err: unknown) {
      const subscriptionError = err instanceof SubscriptionError
        ? err
        : new SubscriptionError(
            err instanceof Error ? err.message : 'Failed to add payment method',
            'ADD_ERROR',
            500
          );
      setError(subscriptionError);
      throw subscriptionError;
    }
  }, [paymentClient, fetchPaymentMethods]);

  // Remove payment method
  const removeMethod = useCallback(async (methodId: string): Promise<void> => {
    if (!paymentClient) {
      throw new SubscriptionError('Remove payment method not available', 'REMOVE_NOT_AVAILABLE', 400);
    }

    try {
      setError(null);

      await paymentClient.removePaymentMethod(methodId);

      // Remove from local state immediately for better UX
      setMethods(prev => prev.filter(method => method.id !== methodId));

      // Refetch to ensure consistency
      await fetchPaymentMethods();
    } catch (err: unknown) {
      const subscriptionError = err instanceof SubscriptionError
        ? err
        : new SubscriptionError(
            err instanceof Error ? err.message : 'Failed to remove payment method',
            'REMOVE_ERROR',
            500
          );
      setError(subscriptionError);

      // Refetch to restore state on error
      await fetchPaymentMethods();
      throw subscriptionError;
    }
  }, [paymentClient, fetchPaymentMethods]);

  // Set default payment method
  const setDefault = useCallback(async (methodId: string): Promise<void> => {
    if (!paymentClient) {
      throw new SubscriptionError('Update default payment method not available', 'UPDATE_NOT_AVAILABLE', 400);
    }

    try {
      setError(null);

      await paymentClient.updateDefaultPaymentMethod(methodId);

      // Update local state immediately for better UX
      setMethods(prev => prev.map(method => ({
        ...method,
        isDefault: method.id === methodId
      })));

      // Refetch to ensure consistency
      await fetchPaymentMethods();
    } catch (err: unknown) {
      const subscriptionError = err instanceof SubscriptionError
        ? err
        : new SubscriptionError(
            err instanceof Error ? err.message : 'Failed to update default payment method',
            'UPDATE_ERROR',
            500
          );
      setError(subscriptionError);

      // Refetch to restore state on error
      await fetchPaymentMethods();
      throw subscriptionError;
    }
  }, [paymentClient, fetchPaymentMethods]);

  // Load payment methods on mount
  useEffect(() => {
    fetchPaymentMethods();
  }, [fetchPaymentMethods]);

  return {
    methods,
    loading,
    error,
    addMethod,
    removeMethod,
    setDefault,
  };
}